import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Film, Link2, Search, ExternalLink } from "lucide-react";
import { api, assetUrl } from "@/lib/api";

const ICON = { pdf: [FileText, "text-amber-400", "White paper (PDF)"], video_upload: [Film, "text-cyan-400", "Uploaded video"], video_url: [Link2, "text-indigo-400", "Video link"] };
const fmtSize = (b) => (b ? (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`) : "—");

export default function Content() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  useEffect(() => { api.get("/content").then((r) => setItems(r.data)); }, []);
  const rows = (items || []).filter((i) => (type === "all" || (type === "pdf" ? i.type === "pdf" : i.type !== "pdf"))
    && (i.title + i.used_in.map((u) => u.page_name + u.program_code).join(" ")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="mb-10 sv-rise">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-indigo-400 mb-3">Content</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Content library</h1>
        <p className="text-sm text-slate-400 mt-3">Every white paper and video hosted on the platform, and the landing pages using it. Upload new content from a landing page editor.</p>
      </div>
      <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/60 sv-rise sv-d1" data-testid="content-table">
        <div className="flex flex-wrap items-center gap-3 justify-between px-6 py-4 border-b border-[#242C3D]">
          <div className="flex gap-2">
            {[["all", "All"], ["pdf", "White papers"], ["video", "Videos"]].map(([v, l]) => (
              <button key={v} data-testid={`content-filter-${v}`} onClick={() => setType(v)}
                className={`h-8 px-3 rounded-full text-xs border ${type === v ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400"}`}>{l}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="content-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-9 pr-3 w-60 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:outline-none focus:border-indigo-500" />
          </div>
        </div>
        {items && rows.length === 0 ? <p className="py-16 text-center text-sm text-slate-500">No content found.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-6 py-3 font-medium">Asset</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium text-right">Size</th><th className="px-4 py-3 font-medium">Used in</th><th className="px-6 py-3 font-medium text-right">Open</th>
            </tr></thead>
            <tbody>
              {rows.map((i) => {
                const [Icon, color, label] = ICON[i.type];
                return (
                  <tr key={i.key} data-testid="content-row" className="border-t border-[#242C3D]">
                    <td className="px-6 py-4"><div className="flex items-center gap-3"><Icon className={`w-4 h-4 shrink-0 ${color}`} /><div className="min-w-0"><div className="font-medium text-slate-100 truncate max-w-xs">{i.title}</div><div className="font-mono text-[11px] text-slate-500 truncate max-w-xs">{i.filename || i.url}</div></div></div></td>
                    <td className="px-4 py-4 text-xs text-slate-400">{label}{i.pages ? ` · ${i.pages} pages` : ""}</td>
                    <td className="px-4 py-4 text-right font-mono text-xs text-slate-400">{fmtSize(i.size)}</td>
                    <td className="px-4 py-4"><div className="flex flex-wrap gap-1.5">{i.used_in.map((u) => (
                      <Link key={u.page_id} to={`/admin/campaigns/${u.page_id}`} className="text-xs px-2 py-1 rounded-md bg-[#0B0F17] border border-[#242C3D] hover:border-indigo-500 text-slate-300">
                        <span className="font-mono text-indigo-300">{u.program_code}</span> · {u.page_name}
                      </Link>))}</div></td>
                    <td className="px-6 py-4 text-right">
                      <a href={i.type === "video_url" ? i.url : assetUrl(i.path)} target="_blank" rel="noreferrer" className="inline-grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5"><ExternalLink className="w-4 h-4" /></a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
